import { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Package,
  AlertTriangle,
  CheckCircle,
  ArrowLeftRight,
  Boxes,
  Calendar,
  FileText,
  User,
  TrendingDown,
  ShoppingCart,
  Loader2,
  ArrowLeft,
  Info,
  LayoutGrid,
  List,
  Plus,
} from 'lucide-react';
import { ProductsService, type KardexLoteItem } from '../services/products/products.service';
import { usePermissions } from '../hooks/usePermissions';

interface KardexProductoItem {
  productoId: string;
  productoNombre: string;
  lotes: number;
  cantidadInicial: number;
  cantidadActual: number;
  totalVendido: number;
  totalVendidoProducto: number;
  totalVendidoPresentaciones: number;
  totalBajas: number;
  esperado: number;
  diferencia: number;
}

// Funciones puras fuera del componente para evitar re-creación en cada render
const formatNumber = (n: number) =>
  new Intl.NumberFormat('es-CO').format(n);

const formatPrice = (n: number) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(n);

export default function KardexLotesPage() {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'lotes' | 'productos'>('productos');

  // Debounce aumentado a 600ms para no saturar el backend
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 600);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['kardex-lotes', debouncedSearch],
    queryFn: async () => {
      const res = await ProductsService.getKardexLotes(undefined, debouncedSearch || undefined);
      return res.data || [];
    },
    staleTime: 30_000,        // 30 segundos sin re-fetch
    gcTime: 5 * 60_000,       // 5 minutos en caché
    placeholderData: keepPreviousData, // Mantiene datos anteriores mientras carga
  });

  const lotes = data || [];

  const productosAgrupados = useMemo(() => {
    const map = new Map<string, KardexProductoItem>();
    for (const lote of lotes) {
      const existente = map.get(lote.productoId);
      if (existente) {
        existente.lotes += 1;
        existente.cantidadInicial += lote.cantidadInicial;
        existente.cantidadActual += lote.cantidadActual;
        existente.totalVendido += lote.totalVendido;
        existente.totalBajas += lote.totalBajas;
        // NO sumar esperado/diferencia por lote — se recalculan al final
        // totalVendidoProducto es el mismo para todos los lotes del mismo producto
        existente.totalVendidoProducto = lote.totalVendidoProducto;
        existente.totalVendidoPresentaciones = lote.totalVendidoPresentaciones;
      } else {
        map.set(lote.productoId, {
          productoId: lote.productoId,
          productoNombre: lote.productoNombre,
          lotes: 1,
          cantidadInicial: lote.cantidadInicial,
          cantidadActual: lote.cantidadActual,
          totalVendido: lote.totalVendido,
          totalVendidoProducto: lote.totalVendidoProducto,
          totalVendidoPresentaciones: lote.totalVendidoPresentaciones,
          totalBajas: lote.totalBajas,
          esperado: 0,   // se recalcula abajo
          diferencia: 0, // se recalcula abajo
        });
      }
    }
    // Recalcular esperado y diferencia a nivel de producto.
    // Usar la suma de totalVendido de los lotes (que ya incluye inferido
    // cuando no hay tracking por lote) en vez de totalVendidoProducto,
    // porque este último puede contar ventas de todos los lotes en cada lote
    // y generar números duplicados o incorrectos.
    for (const prod of map.values()) {
      const vendido = prod.totalVendido;
      prod.esperado = prod.cantidadInicial - vendido - prod.totalBajas;
      prod.diferencia = prod.cantidadActual - prod.esperado;
    }
    return Array.from(map.values()).sort((a, b) => a.productoNombre.localeCompare(b.productoNombre));
  }, [lotes]);

  // Stats memoizado para no recalcular en cada render
  const stats = useMemo(() => ({
    totalProductos: productosAgrupados.length,
    totalLotes: lotes.length,
    conDiferencia: productosAgrupados.filter((p) => p.diferencia !== 0).length,
    totalInicial: productosAgrupados.reduce((sum, p) => sum + p.cantidadInicial, 0),
    totalActual: productosAgrupados.reduce((sum, p) => sum + p.cantidadActual, 0),
    totalVendido: productosAgrupados.reduce((sum, p) => sum + p.totalVendido, 0),
    totalBajas: productosAgrupados.reduce((sum, p) => sum + p.totalBajas, 0),
    totalProducto: productosAgrupados.reduce((sum, p) => sum + p.totalVendido + p.cantidadActual, 0),
  }), [productosAgrupados, lotes.length]);

  if (!hasPermission('Productos', 'ver')) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[calc(100vh-80px)] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                <ArrowLeftRight className="w-6 h-6 text-emerald-600" />
                Kardex de Lotes
              </h1>
            </div>
            <p className="text-gray-600 text-sm">
              Movimiento de inventario por lote: qué entró, qué se vendió, qué se dio de baja y qué queda.
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard/productos')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors self-start sm:self-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a Productos
          </button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          <StatCard icon={Boxes} label="Lotes" value={stats.totalLotes} color="blue" />
          <StatCard icon={Package} label="Ingresado" value={stats.totalInicial} color="emerald" />
          <StatCard icon={ShoppingCart} label="Vendido" value={stats.totalVendido} color="indigo" />
          <StatCard icon={TrendingDown} label="Bajas" value={stats.totalBajas} color="amber" />
          <StatCard icon={Package} label="Actual" value={stats.totalActual} color="teal" />
          <StatCard icon={Plus} label="Total (V+S)" value={stats.totalProducto} color="sky" />
          <StatCard icon={AlertTriangle} label="Con faltante" value={stats.conDiferencia} color="red" />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setActiveTab('productos')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'productos'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
            Por Producto
          </button>
          <button
            onClick={() => setActiveTab('lotes')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'lotes'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <List className="w-4 h-4" />
            Por Lote
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="relative max-w-md w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={activeTab === 'productos' ? 'Buscar producto...' : 'Buscar producto o número de lote...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
            {/* Indicador sutil de búsqueda activa */}
            {isFetching && (
              <div className="absolute inset-y-0 right-8 flex items-center">
                <Loader2 className="h-4 w-4 text-emerald-600 animate-spin" />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
            <Info className="w-4 h-4 text-emerald-600" />
            <span>
              <strong>Ecuación:</strong> Inicial − Vendido − Bajas = Actual
            </span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'productos' ? (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase text-xs">Producto</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs">Lotes</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs">Ingresó</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs text-indigo-700">Vendió (Und)</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs text-violet-700">Vendió (Pres)</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs text-amber-700">Bajas</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs">Queda</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs text-sky-700">Total</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs">Esperado</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs">Diferencia</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading && !lotes.length ? (
              <tr>
                <td colSpan={11} className="px-6 py-12 text-center">
                  <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mx-auto mb-2" />
                  <p className="text-gray-500">Cargando kardex...</p>
                </td>
              </tr>
            ) : productosAgrupados.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-6 py-12 text-center">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No hay productos con lotes registrados</p>
                </td>
              </tr>
            ) : (
              productosAgrupados.map((prod) => (
                <MemoProductoRow key={prod.productoId} producto={prod} />
              ))
            )}
          </tbody>
        </table>
        ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase text-xs">Producto / Lote</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs">Ingresó</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs text-indigo-700">Vendió (Und)</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs text-violet-700">Vendió (Pres)</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs text-amber-700">Bajas</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs">Queda</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs text-sky-700">Total</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs">Esperado</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase text-xs">Diferencia</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500 uppercase text-xs">Costo/u</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase text-xs">Factura / Prov.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading && !lotes.length ? (
              <tr>
                <td colSpan={10} className="px-6 py-12 text-center">
                  <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mx-auto mb-2" />
                  <p className="text-gray-500">Cargando kardex...</p>
                </td>
              </tr>
            ) : lotes.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-6 py-12 text-center">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No hay lotes registrados</p>
                </td>
              </tr>
            ) : (
              lotes.map((lote) => (
                <MemoKardexRow key={lote.loteId} lote={lote} />
              ))
            )}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  color: string;
}) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${colors[color] || colors.blue}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <div className="text-lg font-bold">{formatNumber(value)}</div>
    </div>
  );
}

function ProductoRow({
  producto,
}: {
  producto: KardexProductoItem;
}) {
  const diff = producto.diferencia;
  const diffColor =
    diff === 0
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : diff < 0
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';
  const diffLabel = diff === 0 ? 'OK' : diff > 0 ? `+${formatNumber(diff)}` : formatNumber(diff);

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3">
        <div className="font-bold text-gray-900">{producto.productoNombre}</div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-200">
          {formatNumber(producto.lotes)}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 text-emerald-700 font-bold border border-emerald-200">
          {formatNumber(producto.cantidadInicial)}
        </span>
      </td>
      <td className="px-4 py-3 text-center text-indigo-600 font-bold">{formatNumber(producto.totalVendido)}</td>
      <td className="px-4 py-3 text-center text-violet-600 font-bold">{formatNumber(producto.totalVendidoPresentaciones)}</td>
      <td className="px-4 py-3 text-center text-amber-600 font-bold">{formatNumber(producto.totalBajas)}</td>
      <td className="px-4 py-3 text-center">
        <span className="text-lg font-bold text-gray-900">{formatNumber(producto.cantidadActual)}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-sky-50 text-sky-700 font-bold border border-sky-200">
          {formatNumber(producto.totalVendido + producto.cantidadActual)}
        </span>
      </td>
      <td className="px-4 py-3 text-center text-gray-600">
        <span className="text-sm">{formatNumber(producto.esperado)}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${diffColor}`}>
          {diff === 0 ? (
            <CheckCircle className="w-3 h-3" />
          ) : (
            <AlertTriangle className="w-3 h-3" />
          )}
          {diffLabel}
        </span>
      </td>
    </tr>
  );
}

const MemoProductoRow = memo(ProductoRow);

function KardexRow({
  lote,
}: {
  lote: KardexLoteItem;
}) {
  const diff = lote.diferencia;
  const diffColor =
    diff === 0
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : diff < 0
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';

  const diffLabel = diff === 0 ? 'OK' : diff > 0 ? `+${formatNumber(diff)}` : formatNumber(diff);

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900">{lote.productoNombre}</div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="font-mono text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
            {lote.numeroLote}
          </span>
          <span className="text-xs text-gray-500">{lote.presentacionNombre}</span>
        </div>
        <div className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          Ingresó: {lote.fechaIngreso ? new Date(lote.fechaIngreso).toLocaleDateString('es-CO') : '—'}
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 text-emerald-700 font-bold border border-emerald-200">
          {formatNumber(lote.cantidadInicial)}
        </span>
      </td>
      <td className="px-4 py-3 text-center text-indigo-600 font-bold">{formatNumber(lote.totalVendido)}</td>
      <td className="px-4 py-3 text-center text-violet-600 font-bold">{formatNumber(lote.totalVendidoPresentaciones)}</td>
      <td className="px-4 py-3 text-center text-amber-600 font-bold">{formatNumber(lote.totalBajas)}</td>
      <td className="px-4 py-3 text-center">
        <span className="text-lg font-bold text-gray-900">{formatNumber(lote.cantidadActual)}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-sky-50 text-sky-700 font-bold border border-sky-200">
          {formatNumber(lote.totalVendido + lote.cantidadActual)}
        </span>
      </td>
      <td className="px-4 py-3 text-center text-gray-600">
        <span className="text-sm">{formatNumber(lote.esperado)}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${diffColor}`}>
          {diff === 0 ? (
            <CheckCircle className="w-3 h-3" />
          ) : (
            <AlertTriangle className="w-3 h-3" />
          )}
          {diffLabel}
        </span>
      </td>
      <td className="px-4 py-3 text-right text-gray-600 font-medium">{formatPrice(lote.costoUnitario)}</td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1 text-xs text-gray-600">
            <FileText className="w-3 h-3 text-gray-400" />
            {lote.numeroFactura || '—'}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <User className="w-3 h-3 text-gray-400" />
            {lote.proveedor || '—'}
          </div>
        </div>
      </td>
    </tr>
  );
}

const MemoKardexRow = memo(KardexRow);
